import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  AdvancedMarker,
  APIProvider,
  InfoWindow,
  Map,
  MapCameraChangedEvent,
  Pin,
  useAdvancedMarkerRef,
  useMap,
} from "@vis.gl/react-google-maps";
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { CompanyStore } from "../models/company.store.model";
import { Position } from "../models/base.model";
import { localizedUI } from "./SwitchLanguage";
import moment from "moment";

const StoreInfo = (props: { store: CompanyStore; language: string; onSelectedStore: (store: CompanyStore) => void }) => {
  const { store } = props;
  return (
    <Stack spacing={1} justifyContent={"center"}>
      <Stack spacing={1}>
        <Box>
          {store.name} {store.postCode ? `(${store.postCode})` : ""} - {store.storeId}
        </Box>
        <Stack color={"grey"} direction={"row"} flexWrap={"wrap"}>
          {store.trialDay && (
            <Box>
              {localizedUI(props.language, "trialDay")}: {moment(store.trialDay).format("DD/MM/YYYY")}&nbsp;
            </Box>
          )}
          {store.phoneNumber && (
            <Box>
              {localizedUI(props.language, "phone")}: +{store.phoneNumber}
            </Box>
          )}
        </Stack>
      </Stack>
      <Button
        onClick={() => {
          props.onSelectedStore(store);
        }}
      >
        Select this location
      </Button>
    </Stack>
  );
};

const AdvancedMarkerWithInfo = (props: {
  position?: Position;
  info?: ReactNode;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  return (
    <>
      <AdvancedMarker ref={markerRef} position={props.position} onClick={props.onOpen}>
        <Pin background={primaryColor} glyphColor={"#fff"} borderColor={"#fff"} />
      </AdvancedMarker>
      {props.open && (
        <InfoWindow anchor={marker} onClose={props.onClose}>
          {props.info}
        </InfoWindow>
      )}
    </>
  );
};

interface CompanyStoreGoogleMapProps {
  stores: CompanyStore[];
  onSelectedStore: (store: CompanyStore) => void;
  language: string;
  currentPosition?: Position;
}

const CompanyStoreGoogleMap = (props: CompanyStoreGoogleMapProps) => {
  const defaultPosition = props.stores[0]?.position || {
    lat: 51.507,
    lng: -0.127,
  };
  const [openMarkerIndex, setOpenMarkerIndex] = useState<number | undefined>();
  const map = useMap("map");

  useEffect(() => {
    if (props.currentPosition) {
      map?.setCenter(props.currentPosition);
    }
  }, [map, props.currentPosition]);

  return (
    <Box width={"100%"} height={"100%"} borderRadius={1} overflow={"hidden"}>
      <Map mapId="DEMO_MAP_ID" id="map" defaultZoom={12} defaultCenter={defaultPosition}>
        {props.stores.map((store, index) => (
          <AdvancedMarkerWithInfo
            key={store.id}
            position={store?.position}
            info={<StoreInfo store={store} language={props.language} onSelectedStore={props.onSelectedStore} />}
            open={openMarkerIndex === index}
            onOpen={() => setOpenMarkerIndex(index)}
            onClose={() => setOpenMarkerIndex(undefined)}
          />
        ))}
        <AdvancedMarker key={"current"} position={props.currentPosition}>
          <Pin background={"#4285F4"} glyphColor={"#fff"} borderColor={"#000"} />
        </AdvancedMarker>
      </Map>
    </Box>
  );
};

//use this wrapper as useMap() hook requires inside APIProvider
const Wrapper = (props: CompanyStoreGoogleMapProps) => {
  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY || ""} language={props.language}>
      <CompanyStoreGoogleMap {...props} />
    </APIProvider>
  );
};
export default Wrapper;
